/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DrmType, VideoPlaybackType } from "../enums";
import type {
  TAudioTrack,
  TPlayerStats,
  TTextTrack,
  TVariantTrack,
  TVideoPlaybackStatsList
} from "../types";

/**
 * Inputs required to assemble a {@link TVideoPlaybackStatsList} snapshot.
 *
 * Ported from kaveri-web-app `src/core/videoplayer/utils/VideoPlaybackStats.ts`.
 * Kaveri's version is tightly coupled to the VPC singleton; this version
 * accepts all data as params so it remains decoupled and testable.
 *
 * The shape mirrors the rows of the diagnostics overlay rendered by the
 * UI layer. Anything that isn't yet known is allowed to be `undefined`
 * and is rendered as a sentinel (`-`) to avoid empty cells.
 */
export interface IVideoPlaybackStatsParams {
  audioTrack: TAudioTrack | undefined;
  channelCallSign: string | undefined;
  drmType: DrmType | undefined;
  selectedVariant: TVariantTrack | undefined;
  stats: TPlayerStats | undefined;
  streamUrl: string | undefined;
  textTracks: ReadonlyArray<TTextTrack> | undefined;
  userAgent: string | undefined;
  variants: ReadonlyArray<TVariantTrack> | undefined;
  videoPlaybackType: VideoPlaybackType;
}

const NOT_AVAILABLE = "-";
const NO_TEXT = "No";

const formatString = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return NOT_AVAILABLE;
  }
  return value.length === 0 ? NOT_AVAILABLE : value;
};

/**
 * Assembly utility for the playback stats overlay.
 *
 * Ported from kaveri-web-app. Output formats match kaveri:
 * - resolution: `{height}p{,frameRate fps}`
 * - variants: `{heightXwidth@bandwidth kbps}` with current marked `{*current*}`
 * - droppedFrames: `dropped/decoded`
 * - bufferAvailable: `value S`
 * - ccTracks: `Yes, *selected* other...` or `No`
 *
 * `build` returns a fully-populated {@link TVideoPlaybackStatsList}. Each
 * formatter is also exposed as a `static` so error reporting and telemetry
 * can reuse the same string conventions without re-deriving the snapshot.
 */
export class VideoPlaybackStats {
  /**
   * Format a bandwidth value in bits per second to `<value> kbps`.
   */
  public static formatBandwidthKbps(bps: number | null | undefined): string {
    if (bps === null || bps === undefined || !Number.isFinite(bps)) {
      return NOT_AVAILABLE;
    }
    return `${Math.round(bps / 1000)} kbps`;
  }

  /**
   * Format resolution as kaveri does: `{height}p{,frameRate fps}`.
   * Example: `1080p,30fps` or `720p`.
   */
  public static formatResolution(
    height: number | null | undefined,
    frameRate: number | null | undefined
  ): string {
    if (height === null || height === undefined || !Number.isFinite(height)) {
      return NOT_AVAILABLE;
    }
    const base = `${Math.round(height)}p`;
    if (
      frameRate === null ||
      frameRate === undefined ||
      !Number.isFinite(frameRate)
    ) {
      return base;
    }
    return `${base},${Math.round(frameRate)}fps`;
  }

  /**
   * Combine the active codec string with the DRM type label.
   */
  public static formatCodecAndDrm(
    codecs: string | null | undefined,
    drmType: DrmType | undefined
  ): string {
    const codecStr = formatString(codecs);
    if (drmType === undefined || drmType === DrmType.Clear) {
      return codecStr;
    }
    return `${codecStr} / ${drmType}`;
  }

  /**
   * Build a one-line summary of an audio track for the diagnostics panel.
   */
  public static formatAudioTrack(track: TAudioTrack | undefined): string {
    if (track === undefined) {
      return NOT_AVAILABLE;
    }
    const language = formatString(track.language);
    const channels =
      track.channelsCount === null || track.channelsCount === undefined
        ? NOT_AVAILABLE
        : `${track.channelsCount}ch`;
    const codec = formatString(track.codec);
    return `${language} ${channels} ${codec}`.trim();
  }

  /**
   * Format text tracks as kaveri does:
   * - If tracks available: `Yes, *selected* other...`
   * - If no tracks: `No`
   *
   * The active track is wrapped in `*asterisks*`.
   */
  public static formatTextTracks(
    tracks: ReadonlyArray<TTextTrack> | undefined
  ): string {
    if (tracks === undefined || tracks.length === 0) {
      return NO_TEXT;
    }
    const trackLabels: string[] = [];
    for (const track of tracks) {
      let label = "";
      if (typeof track.language === "string" && track.language.length > 0) {
        label = track.language;
      } else if (typeof track.label === "string" && track.label !== null) {
        label = track.label;
      }
      if (label.length === 0) continue;
      if (track.active) {
        trackLabels.push(`*${label}*`);
      } else {
        trackLabels.push(label);
      }
    }
    if (trackLabels.length === 0) {
      return NO_TEXT;
    }
    return `Yes, ${trackLabels.join(" ")}`;
  }

  /**
   * Render variants in kaveri format: `{heightXwidth@bandwidth kbps}` with
   * the currently selected variant marked as `{*heightXwidth@bandwidth kbps*}`.
   */
  public static formatVariants(
    variants: ReadonlyArray<TVariantTrack> | undefined,
    selectedVariant: TVariantTrack | undefined
  ): string {
    if (variants === undefined || variants.length === 0) {
      return NOT_AVAILABLE;
    }
    const entries: string[] = [];
    let currentEntry = "";
    const selectedBandwidth =
      selectedVariant !== undefined ? selectedVariant.bandwidth : -1;
    const selectedHeight =
      selectedVariant !== undefined ? selectedVariant.height : -1;
    const selectedWidth =
      selectedVariant !== undefined ? selectedVariant.width : -1;

    for (const variant of variants) {
      const h = Number.isFinite(variant.height) ? variant.height : 0;
      const w = Number.isFinite(variant.width) ? variant.width : 0;
      const bw = Number.isFinite(variant.bandwidth)
        ? variant.bandwidth / 1000
        : 0;
      const entry = `${h}X${w}@${bw} kbps`;
      const isSelected =
        variant.height === selectedHeight &&
        variant.width === selectedWidth &&
        variant.bandwidth === selectedBandwidth;
      if (isSelected) {
        currentEntry = `{*${entry}*}`;
      } else {
        entries.push(`{${entry}}`);
      }
    }
    if (currentEntry.length > 0) {
      entries.push(currentEntry);
    }
    if (entries.length === 0) {
      return NOT_AVAILABLE;
    }
    return entries.join(" ");
  }

  /**
   * Format dropped and decoded frames as kaveri does: `dropped/decoded`.
   */
  public static formatDroppedAndDecodedFrames(
    droppedFrames: number | null | undefined,
    decodedFrames: number | null | undefined
  ): string {
    if (
      droppedFrames === null ||
      droppedFrames === undefined ||
      !Number.isFinite(droppedFrames)
    ) {
      return NOT_AVAILABLE;
    }
    const decoded =
      decodedFrames !== null &&
      decodedFrames !== undefined &&
      Number.isFinite(decodedFrames)
        ? decodedFrames
        : 0;
    return `${droppedFrames}/${decoded}`;
  }

  /**
   * Format buffer available as kaveri does: `value S`.
   */
  public static formatBufferAvailable(
    bufferAvailable: number | null | undefined
  ): string {
    if (
      bufferAvailable === null ||
      bufferAvailable === undefined ||
      !Number.isFinite(bufferAvailable)
    ) {
      return NOT_AVAILABLE;
    }
    return `${Math.round(bufferAvailable)} S`;
  }

  /**
   * Compose a complete {@link TVideoPlaybackStatsList} from current player
   * state. Every field is guaranteed to be a non-empty string suitable for
   * direct rendering by the diagnostics overlay.
   */
  public static build(
    params: IVideoPlaybackStatsParams
  ): TVideoPlaybackStatsList {
    const stats = params.stats;
    const variant = params.selectedVariant;

    const resolution = VideoPlaybackStats.formatResolution(
      stats !== undefined ? stats.height : undefined,
      variant !== undefined ? variant.frameRate : undefined
    );

    let bitrateValue: number | undefined;
    if (variant !== undefined) {
      bitrateValue = variant.bandwidth;
    } else if (stats !== undefined) {
      bitrateValue = stats.streamBandwidth;
    }
    const bitrate = VideoPlaybackStats.formatBandwidthKbps(bitrateValue);

    const connectionSpeed = VideoPlaybackStats.formatBandwidthKbps(
      stats === undefined ? undefined : stats.estimatedBandwidth
    );

    const bufferAvailable = VideoPlaybackStats.formatBufferAvailable(
      stats === undefined ? undefined : stats.bufferingTime
    );

    const droppedFrames = VideoPlaybackStats.formatDroppedAndDecodedFrames(
      stats === undefined ? undefined : stats.droppedFrames,
      stats === undefined ? undefined : stats.decodedFrames
    );

    let codecs: string | undefined;
    if (variant !== undefined && variant.codecs.length > 0) {
      codecs = variant.codecs;
    } else if (stats !== undefined) {
      codecs = stats.currentCodecs;
    }

    return {
      audioTrack: VideoPlaybackStats.formatAudioTrack(params.audioTrack),
      biteRate: bitrate,
      bufferAvailable,
      ccTracks: VideoPlaybackStats.formatTextTracks(params.textTracks),
      channelCallSign: formatString(params.channelCallSign),
      codecAndDrm: VideoPlaybackStats.formatCodecAndDrm(codecs, params.drmType),
      connectionSpeed,
      droppedFrames,
      resolutionRate: resolution,
      streamUrl: formatString(params.streamUrl),
      userAgent: formatString(params.userAgent),
      varients: VideoPlaybackStats.formatVariants(
        params.variants,
        params.selectedVariant
      ),
      videoPlaybackType: params.videoPlaybackType
    };
  }
}
